import React from 'react';
import { Box, Typography, Container, Grid } from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SecurityIcon from '@mui/icons-material/Security';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PublicIcon from '@mui/icons-material/Public';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';

const CreatorPayments = () => {
    return (
        <Box className="bg-white min-h-screen font-sans text-gray-900 pt-20">
            {/* 1. Hero Section */}
            <Container maxWidth="lg" sx={{ pt: 6, pb: 10 }}>
                <Grid container spacing={6} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <div className="inline-block px-4 py-1.5 rounded-full bg-emerald-50 text-emerald-700 font-bold text-xs uppercase tracking-wide mb-6 animate-fade-in-up">
                            Finance & Compliance
                        </div>
                        <Typography variant="h1" className="text-4xl lg:text-6xl font-extrabold text-gray-900 leading-tight mb-6 animate-fade-in-up delay-100">
                            Seamless Global <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-teal-600">Creator Payments</span>
                        </Typography>
                        <Typography variant="body1" className="text-lg text-gray-600 mb-8 leading-relaxed animate-fade-in-up delay-200">
                            Pay creators in 150+ countries with a single click. We handle the tax forms, currency conversion, and compliance so you don't have to.
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        {/* Abstract Visual - Wallet/Card */}
                        <Box className="relative w-full aspect-square md:aspect-[4/3] animate-float">
                            <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-3xl border border-white shadow-2xl overflow-hidden flex items-center justify-center">
                                {/* Background Elements */}
                                <div className="absolute inset-0 opacity-60">
                                    <div className="absolute bottom-[20%] left-[10%] w-48 h-48 bg-emerald-400/20 rounded-full blur-3xl animate-pulse-slow"></div>
                                    <div className="absolute top-[10%] right-[20%] w-40 h-40 bg-teal-400/20 rounded-full blur-3xl animate-pulse-slow delay-1000"></div>
                                </div>

                                {/* Center UI Element: Card Visual */}
                                <div className="relative z-10 w-2/3 h-40 bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl shadow-2xl p-6 flex flex-col justify-between transform -rotate-6">
                                    <div className="flex justify-between items-start">
                                        <div className="w-10 h-6 bg-yellow-500/80 rounded-md"></div>
                                        <div className="text-white/50 text-xs tracking-widest">DEBIT</div>
                                    </div>
                                    <div>
                                        <div className="text-white/80 text-sm tracking-widest mb-1">**** **** **** 4298</div>
                                        <div className="flex justify-between items-end">
                                            <div className="text-white/90 text-sm">JOHN DOE</div>
                                            <div className="w-8 h-8 rounded-full bg-red-500/80"></div>
                                        </div>
                                    </div>

                                    {/* Floating Check */}
                                    <div className="absolute -top-6 -right-6 bg-white p-2 rounded-xl shadow-lg border border-emerald-100 flex items-center gap-2 animate-bounce-custom">
                                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                            <CheckCircleIcon className="text-green-600" fontSize="small" />
                                        </div>
                                        <div>
                                            <div className="text-xs text-gray-500">Sent</div>
                                            <div className="text-sm font-bold text-gray-800">$2,450.00</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Floating Elements */}
                                <div className="absolute top-[20%] left-[5%] bg-white p-3 rounded-xl shadow-lg animate-bounce-custom delay-100">
                                    <CurrencyExchangeIcon className="text-emerald-500" />
                                </div>
                                <div className="absolute bottom-[20%] right-[5%] bg-white p-3 rounded-xl shadow-lg animate-bounce-custom delay-500">
                                    <PublicIcon className="text-blue-500" />
                                </div>
                            </div>
                        </Box>
                    </Grid>
                </Grid>
            </Container>

            {/* 2. Features Grid */}
            <Container maxWidth="lg" sx={{ py: 16 }}>
                <div className="text-center mb-12">
                    <Typography variant="h2" className="text-3xl lg:text-4xl font-bold text-gray-900">
                        Financial Infrastructure
                    </Typography>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Feature 1: Global Payouts (Large) */}
                    <div className="md:col-span-2 bg-emerald-950 rounded-3xl p-8 lg:p-10 text-white relative overflow-hidden group hover:shadow-2xl hover:shadow-emerald-900/20 transition-all duration-500 cursor-pointer">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500 rounded-full mix-blend-overlay filter blur-3xl opacity-20 group-hover:opacity-40 transition-opacity duration-500"></div>
                        <div className="relative z-10">
                            <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                                <PublicIcon className="text-white" />
                            </div>
                            <h3 className="text-2xl font-bold mb-4">Global Payouts</h3>
                            <p className="text-emerald-200 mb-6 max-w-md group-hover:text-white transition-colors">
                                Send payments to 190+ countries in 135+ currencies. We support local bank transfers, PayPal, Wire, and more.
                            </p>
                        </div>
                    </div>

                    {/* Feature 2: Compliance */}
                    <div className="bg-white border border-gray-200 rounded-3xl p-8 hover:border-teal-400 hover:shadow-lg transition-all duration-300 group cursor-pointer">
                        <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center mb-6 text-teal-600 group-hover:bg-teal-600 group-hover:text-white transition-colors">
                            <SecurityIcon />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Automated Tax Forms</h3>
                        <p className="text-gray-500 text-sm">We collect W-9, W-8BEN, and other necessary tax documents globally before payout.</p>
                    </div>

                    {/* Feature 3: Invoicing */}
                    <div className="bg-white border border-gray-200 rounded-3xl p-8 hover:border-orange-400 hover:shadow-lg transition-all duration-300 group cursor-pointer">
                        <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center mb-6 text-orange-600 group-hover:bg-orange-600 group-hover:text-white transition-colors">
                            <ReceiptLongIcon />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Consolidated Invoicing</h3>
                        <p className="text-gray-500 text-sm">Receive one single invoice for all your creator payments each month. Simplify your accounting.</p>
                    </div>

                    {/* Feature 4: Budget Management (Wide) */}
                    <div className="md:col-span-2 bg-gray-50 rounded-3xl p-8 lg:p-10 border border-gray-100 hover:border-gray-300 transition-all flex flex-col md:flex-row items-center gap-8 group">
                        <div className="flex-1">
                            <h3 className="text-xl font-bold text-gray-900 mb-3">Budget Control & Permissions</h3>
                            <p className="text-gray-600 text-sm mb-4">Set strict budget limits per campaign. Assign approval roles to ensure no payment goes out without review.</p>
                            <ul className="space-y-2 text-sm text-gray-500">
                                <li className="flex items-center gap-2"><CheckCircleIcon fontSize="small" className="text-emerald-500" /> Multi-level approval</li>
                                <li className="flex items-center gap-2"><CheckCircleIcon fontSize="small" className="text-emerald-500" /> Real-time spend tracking</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </Container>

        </Box>
    );
};

export default CreatorPayments;
